package com.ragnorak.clanybackoffice.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Inventory
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.clany.backoffice.model.SubscriptionPlanResponse
import com.clany.backoffice.model.SubscriptionPriceDto
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.jsonPrimitive
import androidx.compose.ui.platform.LocalFocusManager
import kotlinx.coroutines.launch

@Composable
fun SubscriptionPlansScreen(viewModel: SubscriptionPlansViewModel) {
    val state = viewModel.uiState


    // Plan detail/edit dialog — state lifted to screen level to fix Close button
    state.selectedPlan?.let { plan ->
        PlanDetailDialog(
            plan = plan,
            isSaving = state.isSaving,
            onDismiss = { viewModel.dismissPlan() },
            onSave = { updatedPlan -> viewModel.updatePlan(updatedPlan) }
        )
    }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                Icons.Default.Inventory,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                "Subscription Plans",
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f)
            )

            if (state.isRefreshing) {
                CircularProgressIndicator(modifier = Modifier.size(24.dp))
                Spacer(modifier = Modifier.width(8.dp))
            }

            Button(
                onClick = { viewModel.refreshPlans() },
                enabled = !state.isRefreshing,
                shape = RoundedCornerShape(8.dp)
            ) {
                Icon(
                    Icons.Default.Refresh,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(4.dp))
                Text("Refresh Limits")
            }
        }
        Spacer(modifier = Modifier.height(16.dp))

        OutlinedTextField(
            value = state.searchQuery,
            onValueChange = { viewModel.onSearchQueryChanged(it) },
            placeholder = { Text("Search plans...") },
            leadingIcon = { Icon(Icons.Default.Search, contentDescription = "Search") },
            singleLine = true,
            shape = RoundedCornerShape(12.dp),
            modifier = Modifier.fillMaxWidth()
        )
        Spacer(modifier = Modifier.height(16.dp))

        if (state.isLoading) LinearProgressIndicator(modifier = Modifier.fillMaxWidth())

        state.error?.let {
            Card(
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    it,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                    modifier = Modifier.padding(12.dp)
                )
            }
            Spacer(modifier = Modifier.height(8.dp))
        }

        Card(
            modifier = Modifier.weight(1f),
            shape = RoundedCornerShape(12.dp),
            elevation = CardDefaults.cardElevation(2.dp)
        ) {
            Column {
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .background(MaterialTheme.colorScheme.primaryContainer).padding(12.dp)
                ) {
                    Text(
                        "Name",
                        modifier = Modifier.weight(2f),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                    Text(
                        "Order",
                        modifier = Modifier.weight(1f),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                    Text(
                        "Price",
                        modifier = Modifier.weight(1f),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                    Text(
                        "Currency",
                        modifier = Modifier.weight(1f),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                    Text(
                        "Period",
                        modifier = Modifier.weight(1f),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                    Text(
                        "Limits",
                        modifier = Modifier.weight(1.5f),
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                    Text("", modifier = Modifier.width(40.dp))
                }
                HorizontalDivider()
                if (state.plans.isEmpty() && !state.isLoading) {
                    Box(
                        modifier = Modifier.fillMaxSize().padding(32.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            "No subscription plans found",
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                } else {
                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        items(state.plans) { plan ->
                            Row(
                                modifier = Modifier.fillMaxWidth()
                                    .padding(horizontal = 12.dp, vertical = 8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(plan.name, modifier = Modifier.weight(2f))
                                Text("${plan.sortOrder}", modifier = Modifier.weight(1f))
                                Text(
                                    plan.subscriptionPrice?.price?.toString() ?: "N/A",
                                    modifier = Modifier.weight(1f)
                                )
                                Text(plan.subscriptionPrice?.currency?.ifEmpty { "N/A" } ?: "N/A",
                                    modifier = Modifier.weight(1f))
                                Text(plan.subscriptionPrice?.period?.ifEmpty { "N/A" } ?: "N/A",
                                    modifier = Modifier.weight(1f))
                                Text(
                                    text = plan.limits.entries.take(2)
                                        .joinToString { "${it.key}: ${it.value.jsonPrimitive.content}" } + if (plan.limits.size > 2) "..." else "",
                                    style = MaterialTheme.typography.bodySmall,
                                    modifier = Modifier.weight(1.5f)
                                )
                                IconButton(
                                    onClick = { viewModel.selectPlan(plan) },
                                    modifier = Modifier.width(40.dp)
                                ) {
                                    Icon(
                                        Icons.Default.Edit,
                                        contentDescription = "Edit plan",
                                        modifier = Modifier.size(18.dp),
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                            HorizontalDivider()
                        }
                    }
                }
            }
        }

        // Pagination
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            FilledTonalButton(
                onClick = { viewModel.loadPlans(state.page - 1) },
                enabled = state.page > 1
            ) {
                Icon(
                    Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = "Previous",
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(4.dp))
                Text("Previous")
            }
            Spacer(modifier = Modifier.width(16.dp))
            Text(
                "Page ${state.page}",
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold
            )
            Spacer(modifier = Modifier.width(16.dp))
            FilledTonalButton(
                onClick = { viewModel.loadPlans(state.page + 1) },
                enabled = state.hasMore
            ) {
                Text("Next")
                Spacer(modifier = Modifier.width(4.dp))
                Icon(
                    Icons.AutoMirrored.Filled.ArrowForward,
                    contentDescription = "Next",
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    }
}

@Composable
fun PlanDetailDialog(
    plan: SubscriptionPlanResponse,
    isSaving: Boolean,
    onDismiss: () -> Unit,
    onSave: (SubscriptionPlanResponse) -> Unit
) {
    // Editable state initialized from the plan
    var name by remember(plan.id) { mutableStateOf(plan.name) }
    var sortOrder by remember(plan.id) { mutableStateOf(plan.sortOrder.toString()) }
    var price by remember(plan.id) {
        mutableStateOf(
            plan.subscriptionPrice?.price?.toString() ?: ""
        )
    }
    var currency by remember(plan.id) { mutableStateOf(plan.subscriptionPrice?.currency ?: "") }
    var period by remember(plan.id) { mutableStateOf(plan.subscriptionPrice?.period ?: "") }
    var offerPrice by remember(plan.id) {
        mutableStateOf(
            plan.subscriptionPrice?.offerPrice?.toString() ?: ""
        )
    }
    var offerActive by remember(plan.id) {
        mutableStateOf(
            plan.subscriptionPrice?.offerActive ?: false
        )
    }
    var offerKey by remember(plan.id) {
        mutableStateOf(plan.subscriptionPrice?.offerLabel?.get("key") ?: "")
    }
    val offerTranslations = remember(plan.id) {
        androidx.compose.runtime.snapshots.SnapshotStateList<Pair<String, String>>().also { list ->
            plan.subscriptionPrice?.offerLabel
                ?.filterKeys { it != "key" }
                ?.forEach { (lang, msg) -> list.add(lang to msg) }
        }
    }
    var limitsEdits by remember(plan.id) { mutableStateOf(plan.limits.mapValues { it.value.jsonPrimitive.content }) }
    var featuresEdits by remember(plan.id) { mutableStateOf(plan.features.toMutableMap()) }

    val focusManager = LocalFocusManager.current
    val coroutineScope = rememberCoroutineScope()

    AlertDialog(
        onDismissRequest = {
            focusManager.clearFocus(force = true)
            coroutineScope.launch {
                kotlinx.coroutines.delay(50)
                onDismiss()
            }
        },
        title = { Text("Edit Plan: ${plan.name}", fontWeight = FontWeight.Bold) },
        text = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                // Basic Info
                SectionHeader("Basic Info")
                EditField("Name", name) { name = it }
                EditField("Sort Order", sortOrder, KeyboardType.Number) { sortOrder = it }

                Spacer(modifier = Modifier.height(12.dp))

                // Pricing
                SectionHeader("Pricing")
                EditField("Price", price, KeyboardType.Decimal) { price = it }
                EditField("Currency", currency) { currency = it }
                EditField("Period", period) { period = it }
                EditField("Offer Price", offerPrice, KeyboardType.Decimal) { offerPrice = it }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(checked = offerActive, onCheckedChange = { offerActive = it })
                    Text("Offer Active")
                }
                EditField("Offer Label Key", offerKey) { offerKey = it }
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        "Offer Messages",
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.weight(1f)
                    )
                    IconButton(onClick = { offerTranslations.add("" to "") }) {
                        Icon(Icons.Default.Add, contentDescription = "Add language", tint = MaterialTheme.colorScheme.primary)
                    }
                }
                offerTranslations.forEachIndexed { index, (lang, msg) ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        OutlinedTextField(
                            value = lang,
                            onValueChange = { offerTranslations[index] = it to msg },
                            label = { Text("Lang") },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.width(72.dp).padding(top = 4.dp, bottom = 4.dp, end = 4.dp)
                        )
                        OutlinedTextField(
                            value = msg,
                            onValueChange = { offerTranslations[index] = lang to it },
                            label = { Text("Message") },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.weight(1f).padding(vertical = 4.dp)
                        )
                        IconButton(onClick = { offerTranslations.removeAt(index) }) {
                            Icon(Icons.Default.Close, contentDescription = "Remove", tint = MaterialTheme.colorScheme.error)
                        }
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Limits
                SectionHeader("Limits")
                limitsEdits.forEach { (key, value) ->
                    EditField(key, value) { newVal ->
                        limitsEdits = limitsEdits.toMutableMap().also { it[key] = newVal }
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Features
                SectionHeader("Features")
                featuresEdits.forEach { (key, value) ->
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Checkbox(
                            checked = value,
                            onCheckedChange = { newVal ->
                                featuresEdits =
                                    featuresEdits.toMutableMap().also { it[key] = newVal }
                            }
                        )
                        Text(key)
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    focusManager.clearFocus(force = true)
                    val updatedPlan = plan.copy(
                        name = name,
                        sortOrder = sortOrder.toIntOrNull() ?: plan.sortOrder,
                        subscriptionPrice = run {
                            val offerLabel = buildMap<String, String> {
                                if (offerKey.isNotBlank()) put("key", offerKey)
                                offerTranslations
                                    .filter { (lang, _) -> lang.isNotBlank() }
                                    .forEach { (lang, msg) -> put(lang, msg) }
                            }.ifEmpty { null }
                            plan.subscriptionPrice?.copy(
                                price = price.toFloatOrNull(),
                                currency = currency,
                                period = period,
                                offerPrice = offerPrice.toFloatOrNull(),
                                offerActive = offerActive,
                                offerLabel = offerLabel
                            ) ?: SubscriptionPriceDto(
                                price = price.toFloatOrNull(),
                                currency = currency,
                                period = period,
                                offerPrice = offerPrice.toFloatOrNull(),
                                offerActive = offerActive,
                                offerLabel = offerLabel
                            )
                        },
                        limits = limitsEdits.mapValues {
                            val num = it.value.toIntOrNull()
                            if (num != null) JsonPrimitive(num) else JsonPrimitive(it.value)
                        },
                        features = featuresEdits
                    )
                    onSave(updatedPlan)
                },
                enabled = !isSaving
            ) {
                if (isSaving) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    Spacer(modifier = Modifier.width(8.dp))
                }
                Text("Save")
            }
        },
        dismissButton = {
            Button(onClick = {
                focusManager.clearFocus(force = true)
                onDismiss()
            }, enabled = !isSaving) {
                Text("Cancel")
            }
        }
    )
}

@Composable
private fun SectionHeader(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelLarge,
        color = MaterialTheme.colorScheme.primary,
        fontWeight = FontWeight.Bold,
        modifier = Modifier.padding(vertical = 4.dp)
    )
    HorizontalDivider()
    Spacer(modifier = Modifier.height(8.dp))
}

@Composable
private fun EditField(
    label: String,
    value: String,
    keyboardType: KeyboardType = KeyboardType.Text,
    onValueChange: (String) -> Unit
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        singleLine = true,
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
        shape = RoundedCornerShape(8.dp)
    )
}
